import {
  AlertCard,
  Button,
  Card,
  IconButton,
  Tag,
  TextInput,
} from "@caprikorn/dynamo-ui";
import { type FC, useId } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Link, generatePath, useNavigate } from "react-router-dom";

import { CircleInfo, EditPencil01 } from "@icons";
import { ROUTES } from "@router/router.constants";
import { emailRegex } from "@utils/form";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import classes from "./CreateUserForm.module.css";
import type { CreateUserFormProps } from "./CreateUserForm.types";

export const CreateUserForm: FC<CreateUserFormProps> = ({
  checkEmail,
  data,
  isLoading,
  perimeterAuthorizationsNbr,
  onToggleModal,
  onToggleAuthorizationsDrawer,
  onToggleRoleDrawer,
  resetCheckEmail,
  isAdmin,
}) => {
  const { t } = useCustomTranslation(["main", "common"]);
  const navigate = useNavigate();

  const titleEmailId = useId();
  const titleIdentityId = useId();
  const titleRoleId = useId();
  const titleAuthorizationsId = useId();

  const role = isAdmin ? "admin" : "restricted";
  const alreadyAUser = data?.isAvailable === false;
  const { control, reset } = useFormContext();

  return (
    <div className={classes.container}>
      <Card
        aria-labelledby={titleEmailId}
        as="section"
        className={classes.card}
        size="lg"
      >
        <h2 className="font-title-md" id={titleEmailId}>
          {t("userCreate.email.title")}
        </h2>
        <p>{t("userCreate.email.subtitle")}</p>
        <div className={classes.content}>
          <Controller
            control={control}
            name="email"
            render={({ field, fieldState }) => (
              <TextInput
                {...field}
                className={classes.input}
                disabled={data?.isAvailable || alreadyAUser}
                hasError={Boolean(fieldState.error)}
                hint={
                  fieldState.error?.message &&
                  t(fieldState.error.message, { ns: "common" })
                }
                label={t("form.email.label", { ns: "common" })}
                placeholder={t("form.email.placeholder", { ns: "common" })}
                size="lg"
                type="email"
              />
            )}
            rules={{
              required: "form.error.required",
              pattern: {
                value: emailRegex,
                message: "form.error.invalidFormat",
              },
            }}
          />
        </div>
      </Card>
      {data?.isAvailable === false && (
        <AlertCard>
          <AlertCard.Description>
            {t("userCreate.alreadyUser")}
          </AlertCard.Description>
        </AlertCard>
      )}
      {(!data || data?.isAvailable === false) && (
        <div className={classes.row}>
          <Button
            as={Link}
            data-testid="UserEmailForm-cancel"
            to={ROUTES.ACCESS_MANAGER}
            variant="tertiary"
          >
            {t("button.cancel", { ns: "common" })}
          </Button>
          {!alreadyAUser ? (
            <Button
              data-testid="UserEmailForm-submit"
              disabled={isLoading}
              isLoading={isLoading}
              onClick={checkEmail}
              type="button"
            >
              {t("button.confirm", { ns: "common" })}
            </Button>
          ) : (
            <div className={classes.row}>
              <Button
                data-testid="ButtonContainer-leftButton"
                onClick={() => {
                  reset({ email: "" });
                  resetCheckEmail();
                }}
                variant="secondary"
              >
                {t("userCreate.createAnother")}
              </Button>
              <Button
                data-testid="ButtonContainer-rightButton"
                isLoading={isLoading}
                onClick={() =>
                  navigate(
                    generatePath(ROUTES.ACCESS_MANAGER_USER, {
                      userId: data.id || "",
                    }),
                  )
                }
                type="button"
                variant="primary"
              >
                {t("userCreate.coworker")}
              </Button>
            </div>
          )}
        </div>
      )}
      {data?.isAvailable && (
        <>
          <Card
            aria-labelledby={titleIdentityId}
            as="section"
            className={classes.card}
            size="lg"
          >
            <h2 className="font-title-md" id={titleIdentityId}>
              {t("userCreate.identity.title")}
            </h2>
            <p>{t("userCreate.identity.subtitle")}</p>
            <div className={classes.content}>
              <div className={classes.inputs}>
                <Controller
                  control={control}
                  name="lastName"
                  render={({ field, fieldState }) => (
                    <TextInput
                      {...field}
                      className={classes.input}
                      hasError={Boolean(fieldState.error)}
                      hint={
                        fieldState.error?.message &&
                        t(fieldState.error.message, { ns: "common" })
                      }
                      label={t("userDetails.informations.labels.lastName")}
                      size="lg"
                    />
                  )}
                  rules={{ required: "form.error.required" }}
                />
                <Controller
                  control={control}
                  name="firstName"
                  render={({ field, fieldState }) => (
                    <TextInput
                      {...field}
                      className={classes.input}
                      hasError={Boolean(fieldState.error)}
                      hint={
                        fieldState.error?.message &&
                        t(fieldState.error.message, { ns: "common" })
                      }
                      label={t("userDetails.informations.labels.firstName")}
                      size="lg"
                    />
                  )}
                  rules={{ required: "form.error.required" }}
                />
              </div>
            </div>
          </Card>
          <Card
            aria-labelledby={titleRoleId}
            as="section"
            className={classes.card}
            size="lg"
          >
            <div className={classes.titleWithTooltip}>
              <h2 className="font-title-md" id={titleRoleId}>
                {t("userDetails.role.title")}
              </h2>
              <IconButton
                canShowTooltip
                data-testid="Tooltip-UserRole"
                icon={CircleInfo}
                size="lg"
                tooltipMessage={t(`userDetails.role.tooltip.${role}`)}
                variant="tertiary"
              />
            </div>
            <div className={classes.content}>
              <div className={classes.row}>
                {role === "admin" && (
                  <Tag
                    color="contrast"
                    label={t("userDetails.role.labels.admin")}
                    size="xs"
                  />
                )}
                {role === "restricted" && (
                  <Tag
                    label={t("userDetails.role.labels.restricted")}
                    size="xs"
                  />
                )}
                <Button
                  data-testid="button-edit-userRole"
                  onClick={onToggleRoleDrawer}
                  startIcon={EditPencil01}
                  variant="secondary"
                >
                  {t("button.edit", { ns: "common" })}
                </Button>
              </div>
            </div>
          </Card>
          <Card
            aria-labelledby={titleAuthorizationsId}
            as="section"
            className={classes.card}
            size="lg"
          >
            <div className={classes.titleWithTooltip}>
              <h1 className="font-title-md" id={titleAuthorizationsId}>
                {t("userCreate.perimeter.title")}
              </h1>
              <IconButton
                aria-label="chat"
                canShowTooltip
                icon={CircleInfo}
                size="lg"
                tooltipMessage={t("userCreate.perimeter.tooltip")}
                variant="tertiary"
              />
            </div>
            <div className={classes.content}>
              <AlertCard>
                <AlertCard.Title>
                  {t("userCreate.perimeter.warning")}
                </AlertCard.Title>
                <AlertCard.Description>
                  {t("userCreate.perimeter.admin")}
                </AlertCard.Description>
              </AlertCard>
              <p className="font-body-default">
                {t("userCreate.perimeter.subtitle")}
              </p>
              <div className={classes.footer}>
                <p className="font-body-default">
                  {perimeterAuthorizationsNbr
                    ? t("userDetails.informations.authorization.customer", {
                        count: perimeterAuthorizationsNbr,
                      })
                    : t("userDetails.authorization.error")}
                </p>
                <Button
                  disabled={isAdmin}
                  onClick={onToggleAuthorizationsDrawer}
                  startIcon={EditPencil01}
                  variant="secondary"
                >
                  {t("button.edit", { ns: "common" })}
                </Button>
              </div>
            </div>
          </Card>
          <div className={classes.row}>
            <Button
              as={Link}
              data-testid="CreateUserForm-Cancel"
              to={ROUTES.ACCESS_MANAGER}
              variant="tertiary"
            >
              {t("button.cancel", { ns: "common" })}
            </Button>
            <Button
              data-testid="createUserForm-submit"
              onClick={() => onToggleModal()}
            >
              {t("button.confirm", { ns: "common" })}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
